import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;

    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });

  useEffect(() => {
    // Update document class and localStorage
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    localStorage.setItem('theme', theme);

    // Update CSS variables for dark mode (warm-neutral palette)
    if (theme === 'dark') {
      root.style.setProperty('--color-background', '#171717');
      root.style.setProperty('--color-surface', '#1f1f1f');
      root.style.setProperty('--color-surface-hover', '#262626');
      root.style.setProperty('--color-border', '#2e2e2e');
      root.style.setProperty('--color-text-primary', '#f5f5f5');
      root.style.setProperty('--color-text-secondary', '#c4c4c4');
      root.style.setProperty('--color-text-muted', '#999999');
      root.style.setProperty('--color-primary', '#d8f9b8');
      root.style.setProperty('--color-primary-hover', '#c8e9a8');
      root.style.setProperty('--color-primary-text', '#1a1a1a');
    } else {
      root.style.setProperty('--color-background', '#f8f9fa');
      root.style.setProperty('--color-surface', '#ffffff');
      root.style.setProperty('--color-surface-hover', '#f8f9fa');
      root.style.setProperty('--color-border', '#e5e7eb');
      root.style.setProperty('--color-text-primary', '#111827');
      root.style.setProperty('--color-text-secondary', '#4b5563');
      root.style.setProperty('--color-text-muted', '#9ca3af');
      root.style.setProperty('--color-primary', '#d8f9b8');
      root.style.setProperty('--color-primary-hover', '#c8e9a8');
      root.style.setProperty('--color-primary-text', '#111827');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const setThemeMode = (mode) => {
    if (mode === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setTheme(systemTheme);
    } else {
      setTheme(mode);
    }
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme || savedTheme === 'system') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value = {
    theme,
    toggleTheme,
    setThemeMode,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};